import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { ExportButtons } from '@/components/ExportButtons';
import { ReportTable } from '@/components/ReportTable';
import { apiFetch } from '@/lib/api';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface SupplierPayablesRow {
  supplier_id: number;
  supplier_name: string;
  po_count: number;
  total_billed: number;
  total_paid: number;
  balance_owed: number;
}

interface SupplierPayablesTotals {
  total_billed: number;
  total_paid: number;
  balance_owed: number;
}

interface SupplierPayablesData {
  rows: SupplierPayablesRow[];
  totals: SupplierPayablesTotals;
}

interface SupplierPayablesResponse {
  data: SupplierPayablesData;
}

// ─── Columns ───────────────────────────────────────────────────────────────────

const COLUMNS = [
  { key: 'supplier_name', header: 'Supplier' },
  { key: 'po_count', header: 'PO Count' },
  { key: 'total_billed', header: 'Total Billed', money: true },
  { key: 'total_paid', header: 'Total Paid', money: true },
  { key: 'balance_owed', header: 'Balance Owed', money: true },
];

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function SupplierPayablesReport() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['report', 'supplier-payables'],
    queryFn: () => apiFetch<SupplierPayablesResponse>('/api/reports/supplier-payables'),
  });

  const report = data?.data;
  const rows = (report?.rows ?? []) as unknown as Record<string, unknown>[];
  const totalsRow = report
    ? {
        supplier_name: 'Total',
        total_billed: report.totals.total_billed,
        total_paid: report.totals.total_paid,
        balance_owed: report.totals.balance_owed,
      }
    : undefined;

  return (
    <div className="p-6">
      {/* Back link */}
      <Link
        to="/reports"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3 transition-colors"
      >
        <ChevronLeft className="h-3 w-3" />
        Reports
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Payables</h1>
        <ExportButtons endpoint="/api/reports/supplier-payables" />
      </div>

      {/* Loading / error */}
      {isLoading && <p className="text-sm text-muted-foreground py-10 text-center">Loading…</p>}
      {isError && (
        <p className="text-sm text-destructive py-10 text-center">
          {error instanceof Error ? error.message : 'Failed to load report'}
        </p>
      )}

      {report && (
        <ReportTable columns={COLUMNS} rows={rows} totalsRow={totalsRow} keyField="supplier_id" />
      )}
    </div>
  );
}
